import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapPin, Check, X, Building2, UserRound, Sparkles, BadgeCheck } from "lucide-react";
import ItineraryTimeline from "@/components/ItineraryTimeline";
import BookingCard from "@/components/BookingCard";
import { TrustBadge, ScarcityTag } from "@/components/TrustBadges";
import { Loading } from "@/components/auth/RequireAuth";
import { publicApi, dateRange, VIBE_NAMES, DURATION_NAMES } from "@/lib/api";

function HostStack({ hs, partner }) {
  const rows = [
    { icon: UserRound, role: "Organizer", name: hs.organizer?.name, sub: partner?.verification_status === "verified" ? "Verified partner" : "", verified: partner?.verification_status === "verified" },
    { icon: Building2, role: "Operator / property", name: hs.operator?.name, sub: hs.operator?.property },
    ...(hs.curators || []).map((c) => ({ icon: Sparkles, role: "Curator", name: c.name, sub: c.role })),
  ];
  return (
    <div className="divide-y divide-border rounded-xl border border-border bg-white" data-testid="host-stack">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-4 p-4">
          <r.icon className="h-5 w-5 text-secondary" strokeWidth={1.5} />
          <div className="flex-1">
            <div className="eyebrow text-muted-foreground">{r.role}</div>
            <div className="font-medium">{r.name}</div>
            {r.sub && <div className="text-sm text-muted-foreground">{r.sub}</div>}
          </div>
          {r.verified && <BadgeCheck className="h-5 w-5 text-primary" />}
        </div>
      ))}
    </div>
  );
}

function Policy({ rule }) {
  if (!rule) return null;
  return (
    <div className="rounded-xl border border-border bg-white p-5" data-testid="cancellation-policy">
      <p className="text-sm text-muted-foreground">{rule.policy_text}</p>
      <table className="mt-4 w-full text-sm">
        <tbody>
          {[...rule.tiers].sort((a, b) => b.days_before - a.days_before).map((t) => (
            <tr key={t.days_before} className="border-t border-border">
              <td className="py-2">{t.days_before === 0 ? "Less than the last tier" : `${t.days_before}+ days before start`}</td>
              <td className="py-2 text-right font-semibold">{t.refund_pct}% refund</td>
            </tr>
          ))}
          <tr className="border-t border-border"><td className="py-2">No-show</td><td className="py-2 text-right font-semibold">{rule.no_show_refund_pct}% refund</td></tr>
        </tbody>
      </table>
    </div>
  );
}

const Section = ({ title, kicker, children, id }) => (
  <section className="mt-14" data-testid={id}>
    {kicker && <div className="eyebrow text-secondary">{kicker}</div>}
    <h2 className="mt-1 text-2xl tracking-tight text-primary md:text-3xl">{title}</h2>
    <div className="mt-6">{children}</div>
  </section>
);

export default function ListingDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [missing, setMissing] = useState(false);
  const load = () => publicApi.get(`/listings/${slug}`).then((r) => setData(r.data)).catch(() => setMissing(true));
  useEffect(() => { load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (missing) return <div className="mx-auto max-w-3xl px-6 py-24"><h1 className="text-3xl">This getaway isn't available</h1><Link className="text-secondary" to="/explore">Explore others</Link></div>;
  if (!data) return <Loading />;
  const { listing: l, itinerary, refund_rule, partner } = data;
  return (
    <div data-testid="listing-detail-page">
      <section className="relative h-[52vh] min-h-[380px] overflow-hidden">
        <img src={l.hero_image} alt={l.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-7xl px-5 pb-10 text-white md:px-8">
          <div className="flex flex-wrap items-center gap-2">
            <span className="eyebrow text-white/80">{VIBE_NAMES[l.vibe]} · {DURATION_NAMES[l.duration_type]} · {l.days} day{l.days > 1 ? "s" : ""}</span>
            <ScarcityTag spotsLeft={l.spots_left} capacity={l.capacity} />
          </div>
          <h1 className="mt-3 max-w-4xl text-4xl tracking-tight [text-shadow:0_2px_20px_rgba(0,0,0,0.3)] sm:text-5xl lg:text-6xl" data-testid="listing-title">{l.title}</h1>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-white/85">
            <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" />{l.destination}</span>
            <span>{dateRange(l.start_date, l.end_date)}</span>
            {l.audience === "women-only" && <span>Women-only cohort</span>}
          </div>
        </div>
      </section>
      <div className="mx-auto grid max-w-7xl gap-12 px-5 py-12 md:px-8 lg:grid-cols-[1fr_380px]">
        <div className="min-w-0">
          <p className="max-w-2xl font-display text-xl italic leading-relaxed text-foreground/80 md:text-2xl">{l.tagline}</p>
          <div className="mt-6 flex flex-wrap gap-2">
            {partner?.verification_status === "verified" && <TrustBadge kind="verified" />}
            {partner?.payout_verified && <TrustBadge kind="payout" />}
            <TrustBadge kind="policy" />
            <TrustBadge kind="pkr" />
          </div>
          <Section title="What your days look like" kicker="The timeline" id="timeline-section">
            <ItineraryTimeline blocks={itinerary} startDate={l.start_date} />
          </Section>
          <Section title="The story" id="story-section"><p className="max-w-2xl leading-relaxed text-muted-foreground">{l.description}</p></Section>
          <Section title="Who runs this" kicker="Host stack" id="host-section"><HostStack hs={l.host_stack} partner={partner} /></Section>
          <Section title="Included, and not" id="inclusions-section">
            <div className="grid gap-8 sm:grid-cols-2">
              <ul className="space-y-3">{l.inclusions.map((x) => <li key={x} className="flex gap-3 text-sm"><Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{x}</li>)}</ul>
              <ul className="space-y-3">{l.exclusions.map((x) => <li key={x} className="flex gap-3 text-sm text-muted-foreground"><X className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />{x}</li>)}</ul>
            </div>
          </Section>
          <Section title="Cancellation & no-show policy" id="policy-section"><Policy rule={refund_rule} /></Section>
        </div>
        <aside className="lg:sticky lg:top-24 lg:self-start">
          <BookingCard data={data} onChanged={load} />
        </aside>
      </div>
    </div>
  );
}
